import { createApi } from '@reduxjs/toolkit/query/react';
import { setTokens, logout, setUser } from '../slices/authSlice';
import { baseQueryWithReauth } from './baseQuery';
import { AdminRole } from '../../types/global';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  payload: {
    token: string;
    refreshToken?: string;
    adminData: {
      activeStatus: string;
      email: string;
      firstName: string;
      id: number;
      lastName: string;
      role: AdminRole;
    };
  };
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface RefreshTokenResponse {
  payload: {
    accessToken: string;
    refreshToken?: string;
  };
}

export interface ForgotPasswordRequest {
  email: string;
  otpType: 'FORGET_PASSWORD' | 'RESET_PASSWORD';
}

export interface ForgotPasswordResponse {
  message: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
  confirmPassword: string;
}

export interface ResetPasswordResponse {
  message: string;
}

export interface VerifyOTPRequest {
  code: string;
  email: string;
}

export interface VerifyOTPResponse {
  token: string;
  message: string;
}

export interface GetMeResponse {
  role: {
    id: number;
    code: string;
    name: string;
    scope: string;
  };
  permissions: string[];
  adminData: {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    activeStatus: string | null;
  };
}

export const authService = createApi({
  reducerPath: 'authService',
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => {
    return {
      // get me *************
      getMe: builder.query<GetMeResponse, void>({
        query: () => ({
          url: '/user-service/v1/auth/admin/me',
          method: 'GET',
        }),
        transformResponse: (response: { payload: GetMeResponse }) =>
          response.payload,
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            dispatch(setUser(data));
          } catch (error) {
            dispatch(logout());
          }
        },
      }),
      // login *************
      login: builder.mutation<LoginResponse, LoginRequest>({
        query: (credentials) => ({
          url: '/user-service/v1/auth/admin/login',
          method: 'POST',
          body: credentials,
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            dispatch(
              setTokens({
                token: data.payload.token,
                refreshToken: data.payload.refreshToken,
              })
            );
          } catch (error) {
            console.error('Login error:', error);
          }
        },
      }),
      // logout *************
      logout: builder.mutation<void, void>({
        query: () => ({
          url: '/user-service/v1/auth/logout',
          method: 'POST',
        }),
        async onQueryStarted(_arg, { dispatch }) {
          dispatch(logout());
        },
      }),
      // forgot password *************
      forgotPassword: builder.mutation<
        ForgotPasswordResponse,
        ForgotPasswordRequest
      >({
        query: (data) => ({
          url: '/user-service/v1/auth/admin/send-otp',
          method: 'POST',
          body: data,
        }),
      }),
      // verify OTP *************
      verifyOTP: builder.mutation<VerifyOTPResponse, VerifyOTPRequest>({
        query: (data) => ({
          url: '/user-service/v1/auth/admin/confirm-reset-otp',
          method: 'POST',
          body: data,
        }),
        transformResponse: (response: { payload: VerifyOTPResponse }) =>
          response.payload,
      }),
      // reset password *************
      resetPassword: builder.mutation<
        ResetPasswordResponse,
        ResetPasswordRequest
      >({
        query: (data) => ({
          url: '/user-service/v1/auth/admin/reset-password',
          method: 'POST',
          body: data,
        }),
      }),
      // create password *************
      createPassword: builder.mutation<
        ResetPasswordResponse,
        ResetPasswordRequest
      >({
        query: (data) => ({
          url: '/user-service/v1/auth/admin/create-password',
          method: 'POST',
          body: data,
        }),
      }),
      // refresh token *************
      refreshToken: builder.mutation<RefreshTokenResponse, RefreshTokenRequest>(
        {
          query: ({ refreshToken }) => {
            console.log('Refresh token length:', refreshToken.length);
            return {
              url:
                '/user-service/v1/auth/admin/refresh-token?refreshToken=' +
                refreshToken,
              method: 'POST',
            };
          },
          async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
            try {
              const { data } = await queryFulfilled;
              dispatch(
                setTokens({
                  token: data.payload.accessToken,
                  refreshToken: data.payload.refreshToken,
                })
              );
            } catch (error) {
              // If refresh fails, logout the user
              dispatch(logout());
            }
          },
        }
      ),
    };
  },
});

export const {
  useLoginMutation,
  useLogoutMutation,
  useForgotPasswordMutation,
  useVerifyOTPMutation,
  useResetPasswordMutation,
  useCreatePasswordMutation,
  useRefreshTokenMutation,
  useGetMeQuery,
  useLazyGetMeQuery,
} = authService;
export { logout };
